import React, {useEffect, useState} from "react"
import {Variant} from "@mui/material/styles/createTypography";
import {usePageContext} from "../../../context/PageContext";
import {useParentComponentContext} from "../../../context/ParentComponentContext";
import {renderCharacters} from "../CCOPlainText";
import {
    enrichWithUuid,
    getTextChild,
    ListType,
    renderTruncatedElement,
    RichTextElement,
    RichTextProps,
} from "./CCORichTextHelper";
import {
    renderBlockquoteElement,
    renderDefaultElement,
    renderDivElement,
    renderEmElement,
    renderLiElement,
    renderLinkElement,
    renderMediaElement,
    renderOlElement,
    renderShowMoreContainerElement,
    renderStrongElement,
    renderTextElement,
    renderUlElement
} from "./CCORichTextElementTypes";
import {
    addShowMoreWrapperIfNecessary,
    ElementsToWrap,
    wrapElementsAfterConsecutiveBrTagsInShowMoreAndReturn
} from "./CCORichTextShowMoreWrapper";
import PreviewPropertyHighlighter from "../../preview/PreviewPropertyHighlighter";

const childrenToWrap: ElementsToWrap = {elementsToDisplay: [], elementsToWrap: []}

function CCORichText(props: RichTextProps) {
    const parentContext = useParentComponentContext();
    const pageContext = usePageContext();
    const [isTextEnrichedWithUuids, setIsTextEnrichedWithUuids] = useState(false);
    const [showMore, setShowMore] = useState(false);

    useEffect(() => {
        // Enrich the text with UUIDs only once, before the first render
        if (!isTextEnrichedWithUuids && props.textAsTree) {
            const fullyEnriched = enrichWithUuid(props.textAsTree);
            if (fullyEnriched) {
                setIsTextEnrichedWithUuids(true);
            }
        }
    }, [isTextEnrichedWithUuids, props.textAsTree]);

    if (!isTextEnrichedWithUuids) {
        return null;
    }

    // Reset the elementsToDisplay and elementsToWrap arrays before element rendering so that
    // elements to wrap are not displayed twice (in the element itself and in show more container)
    childrenToWrap.elementsToDisplay.length = 0;
    childrenToWrap.elementsToWrap.length = 0;

    const element = props.charLimit
        ? renderTruncatedElement(props, renderElement, getTextChild, showMore, setShowMore, parentContext?.cmId, pageContext?.page?.id)
        : renderElement(props, props.textAsTree, parentContext?.cmId, pageContext?.page?.id, undefined, props.variant, showMore, setShowMore);

    return <PreviewPropertyHighlighter propertyName={props.propertyName} metadataId={props.metadataId}>{addShowMoreWrapperIfNecessary(props, element, childrenToWrap, renderElement, showMore, setShowMore, parentContext?.cmId, pageContext?.page?.id)}</PreviewPropertyHighlighter>
}

function renderElement(props: RichTextProps,
                       element: RichTextElement,
                       parentComponentId: string,
                       pageId: string,
                       key?: string,
                       variant?: Variant | 'blockQuote',
                       showMore?: boolean,
                       setShowMore?: any,
                       listType?: ListType): JSX.Element | null {

    const wordsLimit = props.wordsLimit;
    const charLimit = props.charLimit;
    const strong = props.strong;
    const color = props.color;

    if (wrapElementsAfterConsecutiveBrTagsInShowMoreAndReturn(props, element, childrenToWrap)) {
        return null
    }

    const renderChildrenRecursively = (e: RichTextElement, index: number) =>
        renderElement(props, e, parentComponentId, pageId, index.toString(), variant, showMore, setShowMore);

    if (!element || !element._type) {
        return null;
    }
    switch (element._type) {
        case "Element": {
            switch (element.name) {
                case "div":
                case "p":
                case "h1":
                case "h2":
                case "h3":
                case "h4":
                case "h5":
                case "h6": {
                    return renderTextElement(element, props, renderChildrenRecursively, key, color, strong)
                }
                case "strong": {
                    return renderStrongElement(element, renderChildrenRecursively)
                }
                case "em": {
                    return renderEmElement(element, renderChildrenRecursively, color)
                }
                case "blockquote": {
                    return renderBlockquoteElement(element, props, renderElement, showMore, setShowMore, parentComponentId, pageId)
                }
                case "ul": {
                    return renderUlElement(element, props, renderElement, showMore, setShowMore, parentComponentId, pageId)
                }
                case "ol": {
                    return renderOlElement(element, props, renderElement, showMore, setShowMore, parentComponentId, pageId)
                }
                case "li": {
                    return renderLiElement(listType, element, renderChildrenRecursively)
                }
                case "a": {
                    return renderLinkElement(element, props, renderChildrenRecursively, key, parentComponentId, pageId)
                }
                case "showMoreWrapper": {
                    return renderShowMoreContainerElement(element, renderChildrenRecursively, showMore, setShowMore, key)
                }
                default: {
                    return renderDefaultElement(element, renderChildrenRecursively)
                }
            }
        }
        case "EmptyElement": {
            switch (element.name) {
                case "br": {
                    return <br key={element.uuid}/>
                }
                case "img": {
                    return renderMediaElement(element, key)
                }
                default: {
                    throw Error(`Unsupported RichTextElement name '${element.name}'`)
                }
            }
        }
        case "Characters": {
            return renderCharacters(element, charLimit, wordsLimit)
        }
        default: {
            switch (element.name) {
                case "div": {
                    return renderDivElement(element, renderChildrenRecursively)
                }
                default: {
                    throw Error(`Unsupported RichTextElement type '${element._type}'`)
                }
            }
        }
    }
}

export default CCORichText